import { useMemo, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

import type { FlowThemeDefinition } from './definition';
import { resolveRenderPolicy, type FlowThemeModeOverride, type RenderPolicy, type RendererClass } from './renderBudget';
import { getRendererClass, readModeOverride, readRenderMemory } from './renderEnvironment';

export interface FlowThemeRenderPolicy extends RenderPolicy {
  rendererClass: RendererClass;
  override: FlowThemeModeOverride | null;
}

/**
 * How `theme` should render on this device, decided once when the component
 * mounts: reduced motion, the QA override, the renderer probe and what this
 * tab learned earlier. Backdrops and admin previews share it, so previews show
 * what learners on the same device would see.
 */
export const useFlowThemeRenderPolicy = (theme: FlowThemeDefinition): FlowThemeRenderPolicy => {
  const reducedMotion = Boolean(useReducedMotion());
  const [environment] = useState(() => ({
    override: readModeOverride(),
    rendererClass: getRendererClass(),
    sessionSlowness: readRenderMemory().slow ?? null,
  }));
  const softwareFriendly = Boolean(theme.softwareFriendly);
  return useMemo(
    () => ({
      ...resolveRenderPolicy({ ...environment, reducedMotion, softwareFriendly }),
      rendererClass: environment.rendererClass,
      override: environment.override,
    }),
    [environment, reducedMotion, softwareFriendly],
  );
};
